import React, { useState, useEffect, Dispatch } from 'react';
import { styles } from '../../styles';
import {
  Stack,
  Box,
  Table,
  TableCell,
  TableRow,
  TableContainer,
  TableHead,
  TableBody,
  Paper,
  Radio
} from '@mui/material';
import StyledModal from '../../../Components/GenericModal/StyledModal';
import ModalFooterButtons from '../../../Components/GenericModal/ModalFooterButtons';
import { DebouncedFormTextField } from '../../../Components/CustomUIElements/FormTextField';
import {
  ITableColumns,
  TemplateTableAction,
  TemplateTableState,
  ITag,
  TAcceptedRecordTypes
} from '../../types';
import GenericDialog from '../../../Components/Modals/GenericDialog';
import { classes } from '../../../Job/Applications/styles';
import Api from '../../API';
import { useMutation } from '@tanstack/react-query';

export default function TagMergerModal({
  modalOpen,
  onClose,
  templatePageState,
  templatePageDispatch,
  refetchTags
}: {
  modalOpen: boolean;
  onClose: () => void;
  templatePageState: TemplateTableState<ITableColumns>;
  templatePageDispatch: Dispatch<TemplateTableAction>;
  refetchTags: () => void;
}) {
  const [tagName, setTagName] = useState<string>('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [confirmationModalOpen, setConfirmationModalOpen] = useState<boolean>(false);
  const [mergeConfirmationModalOpen, setMergeConfirmationModalOpen] = useState<boolean>(false);
  const [primaryTagId, setPrimaryTagId] = useState<number | null>(null);

  const { mutate: mergeTagsMutation, isLoading: submitting } = useMutation({
    mutationFn: (params: { primary_tag_id: number; tag_ids: number[]; new_tag_name: string }) =>
      Api.mergeTags(params),
    onSuccess: () => {
      onClose();
      templatePageDispatch({
        type: 'SET_SNACKBAR',
        payload: { message: 'Tags merged successfully', state: 'success' }
      });
      // Reset to page 1 to refresh the tag list
      templatePageDispatch({ type: 'SET_PAGE', payload: 1 });
      // Reload the page data
      refetchTags();
    },
    onError: () => {
      templatePageDispatch({
        type: 'SET_SNACKBAR',
        payload: { message: 'Failed to merge tags', state: 'error' }
      });
    }
  });

  // Set default tag name when primary tag changes
  useEffect(() => {
    if (primaryTagId) {
      const primaryTag = templatePageState.templateTableState.selectedItems.find(
        (item: TAcceptedRecordTypes) => item.id === primaryTagId
      );
      if (primaryTag) {
        if ('name' in primaryTag) {
          setTagName(primaryTag.name);
        }
      }
    }
  }, [primaryTagId, templatePageState.templateTableState.selectedItems]);

  // Set the first tag as primary by default when modal opens
  useEffect(() => {
    if (
      modalOpen &&
      templatePageState.templateTableState.selectedItems.length > 0 &&
      !primaryTagId
    ) {
      setPrimaryTagId((templatePageState.templateTableState.selectedItems[0] as ITag).id);
    }
  }, [modalOpen, templatePageState.templateTableState.selectedItems, primaryTagId]);

  const handleClose = () => setConfirmationModalOpen(true);

  // Reset modal state when it closes
  useEffect(() => {
    if (!modalOpen) {
      setPrimaryTagId(null);
      setTagName('');
      setErrors({});
      setMergeConfirmationModalOpen(false);
    }
  }, [modalOpen]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!tagName.trim()) {
      newErrors.tagName = 'Tag name is required';
    }

    if (!primaryTagId) {
      newErrors.primaryTag = 'Please select a primary tag';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmitClick = () => {
    if (!validateForm()) return;
    setMergeConfirmationModalOpen(true);
  };

  const submit = () => {
    // Get all selected tag IDs
    const selectedTagIds = templatePageState.templateTableState.selectedItems.map(
      (item: TAcceptedRecordTypes) => item.id
    );

    // Call the mutation to merge tags
    mergeTagsMutation({
      primary_tag_id: primaryTagId as number,
      tag_ids: selectedTagIds,
      new_tag_name: tagName.trim()
    });
  };
  return (
    <StyledModal
      isOpen={modalOpen}
      label="Merge Tags Modal"
      handleClose={handleClose}
      styleOverrides={styles.modalStyleOverrides}
    >
      <Stack sx={styles.modalContainer}>
        <Stack sx={styles.modalTitle}>Merge Tags</Stack>
        <Stack sx={{ rowGap: 2, overflow: 'auto' }}>
          <DebouncedFormTextField
            label="New merged tag name"
            defaultValue={tagName}
            onDebouncedChange={(value: string) => setTagName(value)}
            debounceTime={300}
            styles={{ width: '48%' }}
            required
            error={errors.tagName}
            helperText="Enter a name for the merged tag"
          />
          <Box>
            <h3>Selected Tags</h3>
            <Box sx={{ marginBottom: 2 }}>
              <p>Select a primary tag. Other tags will be merged into this one.</p>
              {errors.primaryTag && (
                <Box sx={{ color: 'error.main', fontSize: '0.75rem', marginTop: 1 }}>
                  {errors.primaryTag}
                </Box>
              )}
            </Box>
            <TableContainer sx={{ boxShadow: 'none' }} component={Paper}>
              <Table
                sx={{ ...classes.selectedApplicantsTable, width: 'unset' }}
                aria-label="selected tags"
              >
                <TableHead>
                  <TableRow>
                    <TableCell>Primary</TableCell>
                    <TableCell>ID</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Candidate Count</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {templatePageState.templateTableState.selectedItems.map(
                    (item: TAcceptedRecordTypes) => (
                      <TableRow key={item.id}>
                        <TableCell>
                          <Radio
                            checked={primaryTagId === item.id}
                            onChange={() => setPrimaryTagId(item.id)}
                            value={item.id}
                            name="primary-tag-radio"
                            inputProps={{
                              'aria-label': `Select ${'name' in item ? item.name : ''} as primary tag`
                            }}
                          />
                        </TableCell>
                        <TableCell>{item.id}</TableCell>
                        <TableCell>{'name' in item ? item.name : ''}</TableCell>
                        <TableCell>
                          {'candidates_count' in item ? item.candidates_count : ''}
                        </TableCell>
                      </TableRow>
                    )
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Stack>
        <ModalFooterButtons
          primaryButtonText={'Confirm'}
          secondaryButtonText="Cancel"
          secondaryButtonCallback={handleClose}
          primaryButtonMinWidth="95px"
          primaryButtonCallback={handleSubmitClick}
          isLoading={submitting}
        />
      </Stack>
      <GenericDialog
        isDialogOpen={confirmationModalOpen}
        setDialogOpen={setConfirmationModalOpen}
        title="Close without saving?"
        description={
          <Stack>
            <Stack>Are you sure you want to cancel?</Stack>
            <Stack>Unsaved changes will be lost.</Stack>
          </Stack>
        }
        buttonText="Yes, close"
        secondaryButtonText="Back to editing"
        url=""
        buttonCallback={() => {
          onClose();
        }}
        callbackLoading={false}
      />
      <GenericDialog
        isDialogOpen={mergeConfirmationModalOpen}
        setDialogOpen={setMergeConfirmationModalOpen}
        title="Confirm Tag Merge"
        description={
          <Stack>
            <Stack>Are you sure you want to merge the selected tags?</Stack>
            <Stack>This action cannot be undone.</Stack>
          </Stack>
        }
        buttonText="Yes, merge tags"
        secondaryButtonText="Cancel"
        url=""
        buttonCallback={() => {
          setMergeConfirmationModalOpen(false);
          submit();
        }}
        callbackLoading={submitting}
      />
    </StyledModal>
  );
}
